import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import portraitBg from '@/assets/bg_portrait.png';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

const Auth = () => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      await authAPI.sendOTP(phone);
      toast.success('OTP sent successfully!');
      setStep(2);
      setResendTimer(30);
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login({ phone, otp });

      const userData = response.data.user;
      const token = response.data.token;

      // Set auth state
      login(userData, token);

      // Wait for state to be set, then navigate
      await new Promise(resolve => setTimeout(resolve, 500));

      if (userData.role === 'provider') {
        navigate('/provider/dashboard');
      } else if (userData.role === 'seeker') {
        navigate('/seeker/dashboard');
      }

      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${portraitBg})` }}
      >
        <div className="absolute inset-0 bg-primary/20"></div>
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-white max-w-md">
            <h2 className="font-heading text-4xl font-bold mb-4">Welcome to HappyTring</h2>
            <p className="text-lg leading-relaxed">Your journey to mental wellness starts here. Connect with compassionate professionals who care.</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2C3333] mb-2">
              Welcome Back
            </h1>
            <p className="text-[#4B5563]">
              Login to your account
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-6" data-testid="auth-form">
              <div>
                <Label htmlFor="phone" className="text-[#2C3333]">Phone Number</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit number"
                  className="mt-2 rounded-lg border-border/60 bg-white/50 focus:ring-2 focus:ring-primary/20 focus:border-primary h-12"
                  maxLength={10}
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="send-otp-btn"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 font-medium transition-all duration-300 shadow-sm hover:shadow-md"
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-8" data-testid="otp-form">
              <div className="flex flex-col items-center">
                <Label htmlFor="otp" className="text-[#2C3333] mb-4 text-center">Verify Phone Number</Label>
                <InputOTP
                  maxLength={4}
                  value={otp}
                  onChange={(value) => setOtp(value)}
                  className="gap-4"
                >
                  <InputOTPGroup className="gap-4">
                    <InputOTPSlot index={0} className="w-12 h-14 text-xl border-border/60 rounded-lg" />
                    <InputOTPSlot index={1} className="w-12 h-14 text-xl border-border/60 rounded-lg" />
                    <InputOTPSlot index={2} className="w-12 h-14 text-xl border-border/60 rounded-lg" />
                    <InputOTPSlot index={3} className="w-12 h-14 text-xl border-border/60 rounded-lg" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  data-testid="verify-otp-btn"
                  disabled={loading || otp.length < 4}
                  className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-6 font-medium transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    disabled={resendTimer > 0 || loading}
                    onClick={handleSendOTP}
                    className={`text-sm font-medium transition-colors ${resendTimer > 0
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-primary hover:text-primary-hover'
                      }`}
                  >
                    {resendTimer > 0
                      ? `Resend OTP in ${resendTimer}s`
                      : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <Button
                type="button"
                data-testid="back-btn"
                onClick={() => setStep(1)}
                variant="ghost"
                className="w-full text-[#4B5563] hover:text-primary hover:bg-secondary/50 pt-2"
              >
                Change Phone Number
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
